import {Inject, Injectable, OnDestroy} from '@angular/core';
import {EmailData, LeadData, LeadDataObject} from "../../../../models/leads/leads.model";
import {ResourceCountry} from "../../../../models/resources/resources.model";
import {Subscription, firstValueFrom} from "rxjs";
import {TranslationDirective} from "../../../shared-module/directives/translation.directive";
import {RESOURCES_DATA_SERVICE, ResourcesDataService} from '../../../../services/resources/resources-data.service';


@Injectable()
export class InfoSideBarEmailService implements OnDestroy {
  selectedLeadDetails: LeadData;
  countries: ResourceCountry[];

  private translationSubscription: Subscription;

  static translationsDownloaded = false;

  // Value editors put into a translation to hide that line from the template.
  // An empty translation cannot be used for this, because it falls back to the key name.
  static readonly HIDDEN_VALUE = '-';

  // =========================
  // language-based cache
  // =========================
  private readonly PAGE_DOMAIN = 'CONTACTS_UI';
  private leadTypesByLang = new Map<string, Promise<Record<string, any>>>();

  constructor(
    @Inject(RESOURCES_DATA_SERVICE) private resourcesDataService: ResourcesDataService,
  ) {
    this.getTranslations();
  }

  ngOnDestroy(): void {
    this.translationSubscription?.unsubscribe();
  }

  /**
   * Loads translations for the given languageCode, builds the leadTypes translations map
   * for that language, then generates the email with those translations.
   */
  async emailExternalDataByLanguageCode(
    languageCode: string,
    email: string,
    lead: LeadDataObject,
    ExternalEmail: string,
    hasB2BKeyword: boolean,
    data: EmailData[],
  ): Promise<string> {
    const leadTypesMap = await this.getLeadTypesTranslationsForLang(languageCode);

    // generate email using the same logic, but with language-specific translations
    return this.emailExternalDataWithLeadTypesMap(leadTypesMap, email, lead, ExternalEmail, hasB2BKeyword, data);
  }

  // =========================
  // Template helpers
  // =========================

  /**
   * Resolves a translation for use in a template.
   * Returns '' for values that mean "nothing to show": missing, blank, the key name
   * (what the translation layer returns for an empty translation) and the HIDDEN_VALUE marker.
   */
  private pick(translations: any, key: string): string {
    const value = translations?.[key];

    if (value === null || value === undefined) {
      return '';
    }

    const trimmed = ('' + value).trim();

    if (!trimmed || trimmed === key || trimmed === InfoSideBarEmailService.HIDDEN_VALUE) {
      return '';
    }

    return '' + value;
  }

  /**
   * Builds a body out of paragraphs. Empty lines are dropped, empty paragraphs disappear
   * completely, so hidden translations leave no blank gaps behind.
   */
  private buildBody(paragraphs: string[][]): string {
    return paragraphs
      .map(paragraph => paragraph.filter(line => !!line && line.trim() !== '').join(`
`))
      .filter(paragraph => paragraph !== '')
      .join(`

`);
  }

  private greeting(lead: LeadDataObject, translations: any): string {
    const start = this.pick(translations, 'EMAIL_TEMPLATE_START_1');

    return start ? `${start.replace('{Lead family name}', lead?.Contact?.LastName ?? '')},` : '';
  }

  // =========================
  // email fill methods
  // =========================

  emailFillDataANGEBOT(
    ExternalEmail: string,
    data: EmailData[],
    lead: LeadDataObject,
    leadNumber: string,
    subject: string,
    body: string,
    email: string,
    translations: any,
  ): string {
    let externalData = '';
    leadNumber = this.selectedLeadDetails?.Lead?.Number;

    // A set ExternalDesc marks the enquiry as a partner lead; without it the lead is Finstral's.
    // OFFER_1 ("an advisor will contact you") and OFFER_2 ("the advisor's details are") describe
    // the two states, so exactly one of them belongs in the mail.
    const isAssignedToDealer = !!lead?.ExternalDesc?.trim();
    const dealerContacts = (data ?? []).filter(( item ): item is EmailData => !!item);

    if (isAssignedToDealer) {
      externalData = `${this.pick(translations, 'EMAIL_TEMPLATE_OFFER_2')}${this.emailDataFormat(dealerContacts)}`;
    }

    if (ExternalEmail) {
      externalData += `
    ${ExternalEmail}
    `;
    }

    subject = this.pick(translations, 'EMAIL_TEMPLATE_SUBJECT');

    const offer3 = this.pick(translations, 'EMAIL_TEMPLATE_OFFER_3');

    body = this.buildBody([
      [this.greeting(lead, translations), this.pick(translations, 'EMAIL_TEMPLATE_START_2')],
      [isAssignedToDealer ? '' : this.pick(translations, 'EMAIL_TEMPLATE_OFFER_1')],
      [externalData],
      [offer3 ? offer3.replace('{ID}', leadNumber) : '', this.pick(translations, 'EMAIL_TEMPLATE_OFFER_4')],
      [this.pick(translations, 'EMAIL_TEMPLATE_END_1'), this.pick(translations, 'EMAIL_TEMPLATE_END_2')],
    ]);

    return `${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  emailFillDataPRODUKTBERATUNG(lead: LeadDataObject, leadNumber: string, subject: string, body: string, email: string, translations: any): string {
    subject = this.pick(translations, 'EMAIL_TEMPLATE_SUBJECT');

    body = this.buildBody([
      [this.greeting(lead, translations), this.pick(translations, 'EMAIL_TEMPLATE_START_2')],
      [
        this.pick(translations, 'EMAIL_TEMPLATE_PRODUCT_ADVICE_1'),
        this.pick(translations, 'EMAIL_TEMPLATE_PRODUCT_ADVICE_2'),
        this.pick(translations, 'EMAIL_TEMPLATE_PRODUCT_ADVICE_3'),
        this.pick(translations, 'EMAIL_TEMPLATE_PRODUCT_ADVICE_4'),
        this.pick(translations, 'EMAIL_TEMPLATE_PRODUCT_ADVICE_5'),
        this.pick(translations, 'EMAIL_TEMPLATE_PRODUCT_ADVICE_6'),
        this.pick(translations, 'EMAIL_TEMPLATE_END_1'),
        this.pick(translations, 'EMAIL_TEMPLATE_END_2'),
      ],
    ]);

    return `${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  emailFillDataBROSCHURE(lead: LeadDataObject, leadNumber: string, subject: string, body: string, email: string, translations: any): string {
    subject = this.pick(translations, 'EMAIL_TEMPLATE_SUBJECT');

    body = this.buildBody([
      [this.greeting(lead, translations), this.pick(translations, 'EMAIL_TEMPLATE_START_2')],
      [this.pick(translations, 'EMAIL_TEMPLATE_BROCHURE_1')],
      [
        this.pick(translations, 'EMAIL_TEMPLATE_END_BROCHURE_SAMPLE'),
        this.pick(translations, 'EMAIL_TEMPLATE_END_2'),
      ],
    ]);

    return `${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  emailFillDataMUSTER(lead: LeadDataObject, leadNumber: string, subject: string, body: string, email: string, translations: any): string {
    subject = this.pick(translations, 'EMAIL_TEMPLATE_SUBJECT');

    body = this.buildBody([
      [this.greeting(lead, translations), this.pick(translations, 'EMAIL_TEMPLATE_START_2')],
      [
        this.pick(translations, 'EMAIL_TEMPLATE_SAMPLE_1'),
        this.pick(translations, 'EMAIL_TEMPLATE_SAMPLE_2'),
        this.pick(translations, 'EMAIL_TEMPLATE_SAMPLE_3'),
      ],
      [
        this.pick(translations, 'EMAIL_TEMPLATE_END_BROCHURE_SAMPLE'),
        this.pick(translations, 'EMAIL_TEMPLATE_END_2'),
      ],
    ]);

    return `${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  emailFillDataALLGEMEIN(lead: LeadDataObject, leadNumber: string, subject: string, body: string, email: string, translations: any): string {
    subject = this.pick(translations, 'EMAIL_TEMPLATE_SUBJECT');

    body = this.buildBody([
      [this.greeting(lead, translations), this.pick(translations, 'EMAIL_TEMPLATE_START_2')],
      [
        this.pick(translations, 'EMAIL_TEMPLATE_GENERAL_1'),
        this.pick(translations, 'EMAIL_TEMPLATE_GENERAL_2'),
        this.pick(translations, 'EMAIL_TEMPLATE_GENERAL_3'),
      ],
      [this.pick(translations, 'EMAIL_TEMPLATE_END_1'), this.pick(translations, 'EMAIL_TEMPLATE_END_2')],
    ]);

    return `${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  emailFillDataAUSSCHREIBUNGSTEXTE(lead: LeadDataObject, leadNumber: string, subject: string, body: string, email: string, translations: any): string {
    subject = this.pick(translations, 'EMAIL_TEMPLATE_SUBJECT');

    body = this.buildBody([
      [this.greeting(lead, translations), this.pick(translations, 'EMAIL_TEMPLATE_START_2')],
      [this.pick(translations, 'EMAIL_TEMPLATE_TENDER_1')],
      [this.pick(translations, 'EMAIL_TEMPLATE_END_1'), this.pick(translations, 'EMAIL_TEMPLATE_END_2')],
    ]);

    return `${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  emailFillDataCADDATEN(lead: LeadDataObject, leadNumber: string, subject: string, body: string, email: string, translations: any): string {
    subject = this.pick(translations, 'EMAIL_TEMPLATE_SUBJECT');

    body = this.buildBody([
      [this.greeting(lead, translations), this.pick(translations, 'EMAIL_TEMPLATE_START_2')],
      [
        this.pick(translations, 'EMAIL_TEMPLATE_CAD_1'),
        this.pick(translations, 'EMAIL_TEMPLATE_CAD_2'),
        this.pick(translations, 'EMAIL_TEMPLATE_CAD_3'),
      ],
      [this.pick(translations, 'EMAIL_TEMPLATE_END_1'), this.pick(translations, 'EMAIL_TEMPLATE_END_2')],
    ]);

    return `${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  emailFillDataREKLAMATION(lead: LeadDataObject, leadNumber: string, subject: string, body: string, email: string, translations: any): string {
    subject = this.pick(translations, 'EMAIL_TEMPLATE_SUBJECT');

    body = this.buildBody([
      [this.greeting(lead, translations), this.pick(translations, 'EMAIL_TEMPLATE_START_2')],
      [this.pick(translations, 'EMAIL_TEMPLATE_CLAIM_1')],
      [this.pick(translations, 'EMAIL_TEMPLATE_END_1'), this.pick(translations, 'EMAIL_TEMPLATE_END_2')],
    ]);

    return `${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  emailFillDataKUNDENDIENST(lead: LeadDataObject, leadNumber: string, subject: string, body: string, email: string, translations: any): string {
    subject = this.pick(translations, 'EMAIL_TEMPLATE_SUBJECT');

    body = this.buildBody([
      [this.greeting(lead, translations), this.pick(translations, 'EMAIL_TEMPLATE_CLIENT_SERVICE_1')],
      [this.pick(translations, 'EMAIL_TEMPLATE_END_1'), this.pick(translations, 'EMAIL_TEMPLATE_END_2')],
    ]);

    return `${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  emailDataFormat(data: EmailData[] | null): string {
    const emailData = data ? data : []

    return emailData.map(item => {
      const countryCode = item?.Address?.CountryCode;
      const currentCountry = this.countries?.find(country => country.IsoCode === countryCode)?.Name;

      return `
    ${item?.Name}
    ${item?.Address?.Address}
    ${item?.Address?.ZipCode} ${item?.Address?.City}
    ${currentCountry}
    ${item?.Address?.Phone}`;
    }).join(`
`);
  }

  emailFillB2BData(lead: LeadDataObject, phoneNumber: string, subject: string, body: string, email: string, translations: any): string {
    subject = this.pick(translations, 'EMAIL_TEMPLATE_SUBJECT_B2B');

    body = this.buildBody([
      [this.greeting(lead, translations)],
      [
        this.pick(translations, 'EMAIL_TEMPLATE_TAG_B2B_1'),
        this.pick(translations, 'EMAIL_TEMPLATE_TAG_B2B_2'),
        this.pick(translations, 'EMAIL_TEMPLATE_TAG_B2B_3'),
        this.pick(translations, 'EMAIL_TEMPLATE_TAG_B2B_4'),
        this.pick(translations, 'EMAIL_TEMPLATE_TAG_B2B_5'),
        this.pick(translations, 'EMAIL_TEMPLATE_TAG_B2B_6'),
      ],
    ]);

    body = body.replace('{PhoneNumber}', phoneNumber);

    return `${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  // =========================
  // UI translations subscription: only marks translations as ready for the side bar
  // =========================
  private getTranslations(): void {
    this.translationSubscription = TranslationDirective.getTranslations().subscribe(() => {
      InfoSideBarEmailService.translationsDownloaded = true;
    });
  }

  // =========================
  // load translations by languageCode and cache per language
  // =========================
  private normalizeLang(code: string): string {
    const c = (code || '').trim().toLowerCase();
    return c || 'en';
  }

  private async getLeadTypesTranslationsForLang(languageCode: string): Promise<Record<string, any>> {
    const lang = this.normalizeLang(languageCode);

    if (!this.leadTypesByLang.has(lang)) {
      this.leadTypesByLang.set(lang, (async () => {
        const res: any = await firstValueFrom(
          this.resourcesDataService.getResourcesTranslationsByLanguageCode(this.PAGE_DOMAIN, lang)
        );

        // adjust if your response shape is different
        const translations: any = (res && res.data) ? res.data : res;

        return this.buildLeadTypesTranslations(translations);
      })());
    }

    return this.leadTypesByLang.get(lang)!;
  }

  private buildLeadTypesTranslations(translations: any): Record<string, any> {
    // every value goes through pick(), so hidden ('-') and empty translations are stored as ''
    const collect = (...keys: string[]): Record<string, string> =>
      keys.reduce((acc, key) => {
        acc[key] = this.pick(translations, key);
        return acc;
      }, {} as Record<string, string>);

    const offerTranslations = collect(
      'EMAIL_TEMPLATE_SUBJECT',
      'EMAIL_TEMPLATE_START_1',
      'EMAIL_TEMPLATE_START_2',
      'EMAIL_TEMPLATE_OFFER_1',
      'EMAIL_TEMPLATE_OFFER_2',
      'EMAIL_TEMPLATE_OFFER_3',
      'EMAIL_TEMPLATE_OFFER_4',
      'EMAIL_TEMPLATE_END_1',
      'EMAIL_TEMPLATE_END_2',
    );

    const productAdviceTranslations = collect(
      'EMAIL_TEMPLATE_SUBJECT',
      'EMAIL_TEMPLATE_START_1',
      'EMAIL_TEMPLATE_START_2',
      'EMAIL_TEMPLATE_PRODUCT_ADVICE_1',
      'EMAIL_TEMPLATE_PRODUCT_ADVICE_2',
      'EMAIL_TEMPLATE_PRODUCT_ADVICE_3',
      'EMAIL_TEMPLATE_PRODUCT_ADVICE_4',
      'EMAIL_TEMPLATE_PRODUCT_ADVICE_5',
      'EMAIL_TEMPLATE_PRODUCT_ADVICE_6',
      'EMAIL_TEMPLATE_END_1',
      'EMAIL_TEMPLATE_END_2',
    );

    const productB2BTranslations = collect(
      'EMAIL_TEMPLATE_SUBJECT_B2B',
      'EMAIL_TEMPLATE_START_1',
      'EMAIL_TEMPLATE_START_2',
      'EMAIL_TEMPLATE_TAG_B2B_1',
      'EMAIL_TEMPLATE_TAG_B2B_2',
      'EMAIL_TEMPLATE_TAG_B2B_3',
      'EMAIL_TEMPLATE_TAG_B2B_4',
      'EMAIL_TEMPLATE_TAG_B2B_5',
      'EMAIL_TEMPLATE_TAG_B2B_6',
      'EMAIL_TEMPLATE_END_1',
      'EMAIL_TEMPLATE_END_2',
    );

    const brochureTranslations = collect(
      'EMAIL_TEMPLATE_SUBJECT',
      'EMAIL_TEMPLATE_START_1',
      'EMAIL_TEMPLATE_START_2',
      'EMAIL_TEMPLATE_BROCHURE_1',
      'EMAIL_TEMPLATE_END_BROCHURE_SAMPLE',
      'EMAIL_TEMPLATE_END_1',
      'EMAIL_TEMPLATE_END_2',
    );

    const sampleTranslations = collect(
      'EMAIL_TEMPLATE_SUBJECT',
      'EMAIL_TEMPLATE_START_1',
      'EMAIL_TEMPLATE_START_2',
      'EMAIL_TEMPLATE_SAMPLE_1',
      'EMAIL_TEMPLATE_SAMPLE_2',
      'EMAIL_TEMPLATE_SAMPLE_3',
      'EMAIL_TEMPLATE_END_BROCHURE_SAMPLE',
      'EMAIL_TEMPLATE_END_1',
      'EMAIL_TEMPLATE_END_2',
    );

    const generalTranslations = collect(
      'EMAIL_TEMPLATE_SUBJECT',
      'EMAIL_TEMPLATE_START_1',
      'EMAIL_TEMPLATE_START_2',
      'EMAIL_TEMPLATE_GENERAL_1',
      'EMAIL_TEMPLATE_GENERAL_2',
      'EMAIL_TEMPLATE_GENERAL_3',
      'EMAIL_TEMPLATE_END_1',
      'EMAIL_TEMPLATE_END_2',
    );

    const tenderTranslations = collect(
      'EMAIL_TEMPLATE_SUBJECT',
      'EMAIL_TEMPLATE_START_1',
      'EMAIL_TEMPLATE_START_2',
      'EMAIL_TEMPLATE_TENDER_1',
      'EMAIL_TEMPLATE_END_1',
      'EMAIL_TEMPLATE_END_2',
    );

    const cadTranslations = collect(
      'EMAIL_TEMPLATE_SUBJECT',
      'EMAIL_TEMPLATE_START_1',
      'EMAIL_TEMPLATE_START_2',
      'EMAIL_TEMPLATE_CAD_1',
      'EMAIL_TEMPLATE_CAD_2',
      'EMAIL_TEMPLATE_CAD_3',
      'EMAIL_TEMPLATE_END_1',
      'EMAIL_TEMPLATE_END_2',
    );

    const claimTranslations = collect(
      'EMAIL_TEMPLATE_SUBJECT',
      'EMAIL_TEMPLATE_START_1',
      'EMAIL_TEMPLATE_START_2',
      'EMAIL_TEMPLATE_CLAIM_1',
      'EMAIL_TEMPLATE_END_1',
      'EMAIL_TEMPLATE_END_2',
    );

    const clientServiceTranslations = collect(
      'EMAIL_TEMPLATE_SUBJECT',
      'EMAIL_TEMPLATE_START_1',
      'EMAIL_TEMPLATE_START_2',
      'EMAIL_TEMPLATE_CLIENT_SERVICE_1',
      'EMAIL_TEMPLATE_END_1',
      'EMAIL_TEMPLATE_END_2',
    );

    return {
      '3': offerTranslations,
      '44': productAdviceTranslations,
      '45': brochureTranslations,
      '46': sampleTranslations,
      '1': generalTranslations,
      '47': tenderTranslations,
      '64': cadTranslations,
      '24': claimTranslations,
      '4': clientServiceTranslations,
      'b2b': productB2BTranslations,
    };
  }

  /**
   * Shared “engine” that generates email using a provided leadTypes map.
   */
  private emailExternalDataWithLeadTypesMap(
    leadTypesMap: Record<string, any>,
    email: string,
    lead: LeadDataObject,
    ExternalEmail: string,
    hasB2BKeyword: boolean,
    data: EmailData[],
  ): string {
    // keep the same guard: if no translations at all yet, return empty
    if (!leadTypesMap) {
      return email ?? '';
    }

    const leadTypeId = hasB2BKeyword ? 'b2b' : '' + lead?.LeadTypeId;

    const translations = leadTypesMap[leadTypeId];
    if (!translations) {
      return email ?? '';
    }

    let leadNumber = '';
    let subject = '';
    let body = '';

    if (hasB2BKeyword) {
      const phoneNumber = lead?.Contact?.Phone ?? ''
      return this.emailFillB2BData(lead, phoneNumber, subject, body, email, translations);
    }

    switch (String(leadTypeId)) {
      case '3':
        return this.emailFillDataANGEBOT(ExternalEmail, data, lead, leadNumber, subject, body, email, translations);
      case '44':
        return this.emailFillDataPRODUKTBERATUNG(lead, leadNumber, subject, body, email, translations);
      case "45":
        return this.emailFillDataBROSCHURE(lead, leadNumber, subject, body, email, translations);
      case "46":
        return this.emailFillDataMUSTER(lead, leadNumber, subject, body, email, translations);
      case "1":
        return this.emailFillDataALLGEMEIN(lead, leadNumber, subject, body, email, translations);
      case "47":
        return this.emailFillDataAUSSCHREIBUNGSTEXTE(lead, leadNumber, subject, body, email, translations);
      case "64":
        return this.emailFillDataCADDATEN(lead, leadNumber, subject, body, email, translations);
      case "24":
        return this.emailFillDataREKLAMATION(lead, leadNumber, subject, body, email, translations);
      case "4":
        return this.emailFillDataKUNDENDIENST(lead, leadNumber, subject, body, email, translations);
    }

    return email ?? '';
  }
}
